/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchProductCombos, type ProductCombo } from '../../../catalog/public-api';
import { ActivityIndicator, FlatList, Text, View } from 'react-native';
import { webDarkScrollProps, webDarkScrollStyle } from '../../../shared/theme/webScrollbar';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { useTabWorkspaceLayout } from '../../../shared/layout/useContentLayout';
import { styles } from '../posShared.styles';
import type { CartItem } from '../../store/usePosStore';
import type { CartEvaluationResult, PromotionWithTargets } from '../../../promotions/public-api';
import { PosCartLineRow } from './PosCartLineRow';
import { PosPromoActionsBar } from './PosPromoActionsBar';
import { PosParkedSaleBar } from './PosParkedSaleBar';
import { PosCartFooter, PosCartTableActions } from './PosCartFooter';
import { useWebPosWriteGate } from '../../application/useWebPosWriteGate';
import { PosCartHeader } from './PosCartHeader';
import { PosCartEmptyState } from './PosCartEmptyState';
import { PosCartCollapseToggle } from './PosCartCollapseToggle';
import type { CartPromoUiStatus } from '../../application/posCartPromoPresentation';
import type { PosStockWarningDetail } from '../../domain/posTypes';
import { usePosCartContextOptional } from '../contexts/PosCartContext';
import { usePosCartSectionLayout } from './usePosCartSectionLayout';
import { ensureTicketFonts } from '../../../shared/ticket/ensureTicketFonts';
import type { PosCartSectionProps } from './posCartSectionProps';
export type { PosCartSectionProps } from './posCartSectionProps';

export const PosCartSection = (props: PosCartSectionProps) => {
  const { chargeBlocked } = useWebPosWriteGate();
  const ctx = usePosCartContextOptional();
  const {
    cart = [],
    total = 0,
    discountTotal,
    cartEvaluation,
    canEdit = false,
    isSaving = false,
    onRemoveFromCart = () => {},
    onIncrementCartItem = () => {},
    onSetCartLineQuantity = () => {},
    onRemoveCartLine = () => {},
    onSetCartLineAttribution = () => {},
    onOpenCheckout = () => {},
    hideDeskHeader = false,
    linesScrollMaxHeight: linesScrollMaxHeightProp,
    showPromoActions = false,
    hasManualCodePromotions = false,
    hasCashierSelectPromotions = false,
    promoCodeInput = '',
    onPromoCodeInputChange,
    appliedPromoCode = null,
    appliedPromoCodes,
    appliedManualPromotionId = null,
    appliedManualPromotionIds,
    promoCodeError = null,
    onApplyPromoCode,
    onClearAppliedPromoCode,
    promoIsApplying = false,
    promoIsClearing = false,
    selectedCashierPromotionId = null,
    cashierEligiblePromotions = [],
    inactiveCashierPromoHints = [],
    onSelectCashierPromotion,
    promoUiStatus,
    onClearPromoSelection,
    canParkCurrentSale = false,
    hasParkedSale = false,
    parkedLineCount = 0,
    parkedTotal = 0,
    onParkCurrentSale,
    onResumeParkedSale,
    onDiscardParkedSale,
    stockWarningByProductId,
    stockWarningsLoading = false,
    onOpenScanner,
    emptyCartScanLabel = 'Escanear',
    onOpenVarious,
    selectedDiningTableName,
    onChangeDiningTable,
    onSendCourseToKitchen,
    canSendCourseToKitchen = false,
    onPrintPreCuenta,
    onSharePreCuenta,
    canPrintPreCuenta = false,
    printTicketIsPreCuenta = false,
    printTicketCounterSummary = false,
    onOpenSplitBill,
    canSplitBill = false,
    inSheet = false,
  } = {
    ...(ctx ?? {}),
    ...props,
  };
  const { layoutWide: isWideLayout } = useTabWorkspaceLayout();
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [combosByProduct, setCombosByProduct] = useState<Map<string, ProductCombo>>(new Map());
  const cartProductIds = useMemo(
    () => (cart ?? []).map((i) => i.productId).filter((id): id is string => Boolean(id)),
    [cart],
  );
  // Audit #15: "Incluye: 2× Papas · 1× Coca" bajo el nombre del combo en la línea.
  useEffect(() => {
    let alive = true;
    void fetchProductCombos(cartProductIds)
      .then((map) => { if (alive) setCombosByProduct(map); })
      .catch(() => setCombosByProduct(new Map()));
    return () => { alive = false; };
  }, [cartProductIds]);

  // Composición EFECTIVA: si la línea eligió un modifier que sustituye un
  // componente, mostrar el elegido (kitchen truth), no la base.
  const comboLabelFor = useCallback(
    (item: CartItem): string | null => {
      const combo = combosByProduct.get(item.productId);
      if (!combo) return null;
      const subs = new Map<string, string>();
      for (const o of item.menuOptions ?? []) {
        if (o.substitutesProductId) subs.set(o.substitutesProductId, o.choiceName);
      }
      const txt = combo.components
        .map((c) => `${c.qty}× ${subs.get(c.productId) ?? c.name ?? c.productId}`)
        .join(' · ');
      return txt || null;
    },
    [combosByProduct],
  );

  const useTicketLines = inSheet || isWideLayout;

  useEffect(() => {
    ensureTicketFonts();
  }, []);

  useEffect(() => {
    if (!inSheet) {
      setSelectedLineId(null);
      return;
    }
    if (selectedLineId && !cart.some((line) => line.lineId === selectedLineId)) {
      setSelectedLineId(cart[0]?.lineId ?? null);
    }
  }, [cart, inSheet, selectedLineId]);

  const {
    isCompactCart,
    useMobileCartCompact,
    useCompactRows,
    setCartContentWidth,
    cartLinesExpanded,
    setCartLinesExpanded,
    showCartCollapse,
    cartLinesCollapsed,
    linesScrollMaxHeight,
    evaluatedByLineId,
    itemCountLabel,
    cartBadgePulse,
    savingsLabel,
  } = usePosCartSectionLayout({
    cart,
    discountTotal,
    cartEvaluation,
    inSheet,
    linesScrollMaxHeightProp,
  });

  const promoBar = (
    <PosPromoActionsBar
      show={showPromoActions}
      hasManualCodePromotions={hasManualCodePromotions}
      hasCashierSelectPromotions={hasCashierSelectPromotions}
      promoCodeInput={promoCodeInput}
      onPromoCodeInputChange={onPromoCodeInputChange ?? (() => {})}
      appliedPromoCode={appliedPromoCode}
      appliedPromoCodes={appliedPromoCodes}
      appliedManualPromotionId={appliedManualPromotionId}
      appliedManualPromotionIds={appliedManualPromotionIds}
      promoCodeError={promoCodeError}
      onApplyPromoCode={onApplyPromoCode ?? (() => {})}
      onClearAppliedPromoCode={onClearAppliedPromoCode ?? (() => {})}
      isApplying={promoIsApplying}
      isClearing={promoIsClearing}
      selectedCashierPromotionId={selectedCashierPromotionId}
      cashierEligiblePromotions={cashierEligiblePromotions}
      inactiveCashierPromoHints={inactiveCashierPromoHints}
      onSelectCashierPromotion={onSelectCashierPromotion ?? (() => {})}
      promoUiStatus={promoUiStatus}
      onClearPromoSelection={onClearPromoSelection ?? (() => {})}
      cartEvaluation={cartEvaluation}
      collapsePanelByDefault={useMobileCartCompact || inSheet}
      variant={useTicketLines ? 'ticket' : 'default'}
    />
  );

  const renderCartLine = (item: CartItem, index: number) => (
    <PosCartLineRow
      key={item.lineId}
      item={item}
      index={index}
      isLast={index === cart.length - 1}
      isCompactCart={useCompactRows}
      inSheet={inSheet}
      ticketLayout={useTicketLines}
      selected={inSheet && selectedLineId === item.lineId}
      onSelect={inSheet ? setSelectedLineId : undefined}
      evaluatedLine={evaluatedByLineId.get(item.lineId)}
      comboLabel={comboLabelFor(item)}
      stockWarning={stockWarningByProductId?.get(item.productId)}
      canEdit={canEdit}
      onRemoveFromCart={onRemoveFromCart}
      onIncrementCartItem={onIncrementCartItem}
      onSetCartLineQuantity={onSetCartLineQuantity}
      onRemoveCartLine={onRemoveCartLine}
      onSetCartLineAttribution={onSetCartLineAttribution}
    />
  );

  const cartHeader = (
    <PosCartHeader
      itemCountLabel={itemCountLabel}
      cartBadgePulse={cartBadgePulse}
      cartLength={cart.length}
      isCompactCart={isCompactCart}
      canEdit={canEdit}
      canParkCurrentSale={canParkCurrentSale}
      hasParkedSale={hasParkedSale}
      onParkCurrentSale={onParkCurrentSale}
      selectedDiningTableName={selectedDiningTableName}
      onChangeDiningTable={onChangeDiningTable}
      inSheet={inSheet}
      deskChrome={isWideLayout && !hideDeskHeader}
    />
  );

  return (
    <View
      style={[
        styles.cartCanvasCard,
        isWideLayout && styles.cartCanvasCardDesk,
        isCompactCart && styles.cartCanvasCardCompact,
        inSheet && styles.cartCanvasCardSheet,
        isWideLayout && !inSheet && styles.posCartDeskSectionStack,
      ]}
      accessibilityRole="summary"
    >
      {!inSheet && !hideDeskHeader ? cartHeader : null}
      <PosParkedSaleBar
        canEdit={canEdit}
        hasParkedSale={hasParkedSale}
        parkedLineCount={parkedLineCount}
        parkedTotal={parkedTotal}
        onResumeParkedSale={onResumeParkedSale ?? (() => {})}
        onDiscardParkedSale={onDiscardParkedSale ?? (() => {})}
      />
      {stockWarningsLoading && cart.length > 0 ? (
        <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 4, paddingHorizontal: 12 }}>
          <ActivityIndicator size="small" />
          <Text style={{ fontSize: 12, marginLeft: 6, color: ticketColors.ink3 }}>Verificando stock…</Text>
        </View>
      ) : null}
      {cart.length === 0 ? (
        <PosCartEmptyState
          isWideLayout={isWideLayout}
          inSheet={inSheet}
          canEdit={canEdit}
          onOpenScanner={onOpenScanner}
          scanActionLabel={emptyCartScanLabel}
        />
      ) : (
        <View
          style={[
            styles.cartCanvasBody,
            isWideLayout && styles.cartCanvasBodyDesk,
            isCompactCart && styles.cartCanvasBodyCompact,
            inSheet && styles.cartCanvasBodySheet,
          ]}
          onLayout={(e) => setCartContentWidth(e.nativeEvent.layout.width)}
        >
          {!inSheet && isCompactCart ? promoBar : null}
          {showCartCollapse ? (
            <PosCartCollapseToggle
              expanded={cartLinesExpanded}
              onToggle={() => setCartLinesExpanded((v) => !v)}
              useMobileCartCompact={useMobileCartCompact}
              itemCountLabel={itemCountLabel}
              total={total}
            />
          ) : null}
          <FlatList
            {...webDarkScrollProps()}
            data={cart}
            keyExtractor={(item) => item.lineId}
            style={[
              styles.cartLinesScroll,
              webDarkScrollStyle,
              // Sheet y dock PC: la lista llena el body y scrollea interno — el CTA
              // queda pineado abajo y nunca se clipea en viewports bajos.
              inSheet || isWideLayout
                ? { flexGrow: 1, flexShrink: 1, flexBasis: 0, flex: 1 }
                : { maxHeight: cartLinesCollapsed ? 0 : linesScrollMaxHeight },
              !inSheet && cartLinesCollapsed && { opacity: 0 },
            ]}
            contentContainerStyle={styles.cartLinesScrollContent}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={inSheet || !cartLinesCollapsed}
            scrollEnabled={inSheet || !cartLinesCollapsed}
            renderItem={({ item, index }) => renderCartLine(item, index)}
            ListFooterComponent={
              <>
                {inSheet || !isCompactCart ? promoBar : null}
                {!inSheet ? (
                  <PosCartTableActions
                    kind="scroll"
                    isSaving={isSaving}
                    canEdit={canEdit}
                    onOpenSplitBill={onOpenSplitBill}
                    canSplitBill={canSplitBill}
                    onPrintPreCuenta={onPrintPreCuenta}
                    onSharePreCuenta={onSharePreCuenta}
                    canPrintPreCuenta={canPrintPreCuenta}
                    printTicketIsPreCuenta={printTicketIsPreCuenta}
                    printTicketCounterSummary={printTicketCounterSummary}
                    onSendCourseToKitchen={onSendCourseToKitchen}
                    canSendCourseToKitchen={canSendCourseToKitchen}
                  />
                ) : null}
              </>
            }
          />
        </View>
      )}
      {cart.length > 0 ? (
        <View style={inSheet ? { flexShrink: 0 } : undefined}>
          <PosCartFooter
            total={total}
            savingsLabel={savingsLabel}
            isSaving={isSaving}
            canEdit={canEdit}
            onOpenCheckout={onOpenCheckout}
            onSendCourseToKitchen={onSendCourseToKitchen}
            canSendCourseToKitchen={canSendCourseToKitchen}
            onPrintPreCuenta={onPrintPreCuenta}
            onSharePreCuenta={onSharePreCuenta}
            canPrintPreCuenta={canPrintPreCuenta}
            printTicketIsPreCuenta={printTicketIsPreCuenta}
            printTicketCounterSummary={printTicketCounterSummary}
            onOpenSplitBill={onOpenSplitBill}
            canSplitBill={canSplitBill}
            useMobileCartCompact={useMobileCartCompact}
            isCompactCart={isCompactCart}
            inSheet={inSheet}
            chargeBlocked={chargeBlocked}
            promoStaleBlocked={promoUiStatus?.hasStaleSelection === true}
          />
        </View>
      ) : null}
    </View>
  );
};
